import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, LogIn } from "lucide-react";
import { api, setToken, setUser } from "../api";
import { PasswordInput } from "../components/PasswordInput";
import { AuthLayout } from "../components/AuthLayout";

function isEmailShaped(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

export function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const justReset = params.get("reset") === "1";
  const justInvited = params.get("invited") === "1";
  const nextPath = params.get("next");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState({ email: false, password: false });

  const emailErr =
    touched.email && email.length > 0 && !isEmailShaped(email)
      ? "That doesn't look like an email address."
      : null;
  const passwordErr =
    touched.password && password.length === 0 ? "Enter your password." : null;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (!isEmailShaped(email) || password.length === 0) {
      setError("Fill in your email and password to continue.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.login(email.trim(), password);
      setToken(res.access_token);
      setUser({ role: res.role, tenant_id: res.tenant_id, email: email.trim(), demo_workspace: res.demo_workspace === true });
      const dest = nextPath && nextPath.startsWith("/") ? nextPath
        : res.role === "superadmin" ? "/superadmin" : "/dashboard";
      navigate(dest);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to your SmartComply workspace.">
      {(justReset || justInvited) && (
        <div className="mb-4 flex items-start gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
          <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            {justReset
              ? "Password updated. Sign in with your new password."
              : "Account ready. Sign in with the password you just set."}
          </span>
        </div>
      )}

      <form onSubmit={onSubmit} className="card" noValidate>
        <div className="card-body space-y-4">
          <div>
            <label htmlFor="login-email" className="block text-sm font-medium text-slate-700 mb-1">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              className={`input ${emailErr ? "!ring-rose-400 !border-rose-400" : ""}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
              autoFocus
              autoComplete="username"
              placeholder="you@example.com"
              aria-invalid={!!emailErr}
              aria-describedby={emailErr ? "login-email-err" : undefined}
              required
            />
            {emailErr && (
              <p id="login-email-err" className="mt-1 text-xs text-rose-700">{emailErr}</p>
            )}
          </div>

          <PasswordInput
            id="login-password"
            label="Password"
            value={password}
            onChange={(v) => { setPassword(v); if (!touched.password) setTouched((t) => ({ ...t, password: true })); }}
            autoComplete="current-password"
            disabled={submitting}
            error={passwordErr}
          />

          <div className="flex items-center justify-end">
            <Link to="/forgot-password" className="text-sm text-brand-600 hover:text-brand-700 hover:underline">
              Forgot password?
            </Link>
          </div>

          {error && (
            <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded">
              {error}
            </div>
          )}

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? (
              <><span className="spinner" aria-hidden="true" /> Signing in…</>
            ) : (
              <><LogIn className="h-4 w-4" aria-hidden="true" /> Sign in</>
            )}
          </button>
        </div>
      </form>

      <p className="mt-6 text-center text-xs text-slate-500">
        Need help signing in? Contact your workspace administrator.
      </p>
    </AuthLayout>
  );
}
